import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import BaseLayout from './BaseLayout';
import FlameLoader from './FlameLoader';
import axios from 'axios';
import { API_BASE } from '../api';
import { getLogoPath } from '../teamUtils';
import { logEvent } from '../apiUtils';
import '../App.css';

// Our four supported leagues with their TheSportsDB IDs
const SUPPORTED_LEAGUES = {
  '4387': { sport: 'NBA',  name: 'NBA'                   },
  '4328': { sport: 'EPL',  name: 'English Premier League' },
  '4346': { sport: 'MLS',  name: 'MLS'                   },
  '4424': { sport: 'MLB',  name: 'MLB'                   },
};

const LeaguePage = () => {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [teamSearch, setTeamSearch] = useState('');

  useEffect(() => {
    document.body.classList.add('league-page-bg');
    logEvent('visited_league');
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_BASE}/sports/league/${leagueId}`);
        setData(res.data);
      } catch (e) {
        if (e.response?.status === 404) setError('League not found.');
        else setError('Failed to load league data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    return () => document.body.classList.remove('league-page-bg');
  }, [leagueId]);

  if (loading) {
    return (
      <BaseLayout>
        <div className="entity-page-container">
          <FlameLoader text="Loading league..." />
        </div>
      </BaseLayout>
    );
  }

  if (error) {
    return (
      <BaseLayout>
        <div className="entity-page-container">
          <p className="error-text">{error}</p>
        </div>
      </BaseLayout>
    );
  }

  if (!data) return null;

  const { league, teams, top_pickers } = data;
  const isFlamePicks = !!SUPPORTED_LEAGUES[leagueId];

  const filteredTeams = teams?.filter((t) =>
    t.strTeam?.toLowerCase().includes(teamSearch.toLowerCase())
  ) || [];

  return (
    <BaseLayout>
      <div className="entity-page-container">

        {/* ── League Header ── */}
        <div className="entity-header">
          {league?.strBadge ? (
            <img
              src={`${league.strBadge}/medium`}
              alt={league.strLeague}
              className="entity-logo entity-logo-league"
            />
          ) : (
            <div className="entity-logo-placeholder" />
          )}
          <div className="entity-header-info">
            <h1 className="entity-page-name">{league?.strLeague}</h1>
            <div className="entity-meta-row">
              {league?.strCountry && (
                <span className="entity-meta-tag">{league.strCountry}</span>
              )}
              {league?.strSport && (
                <span className="entity-meta-tag">{league.strSport}</span>
              )}
              {isFlamePicks && (
                <span className="entity-meta-tag fp-supported-badge">
                  <i className="fa fa-fire" /> FlamePicks
                </span>
              )}
            </div>
            <div className="entity-stat-pills">
              {league?.strCurrentSeason && (
                <span className="entity-stat-pill">
                  <span className="pill-label">Season</span>
                  {league.strCurrentSeason}
                </span>
              )}
              {teams?.length > 0 && (
                <span className="entity-stat-pill">
                  <span className="pill-label">Teams</span>
                  {teams.length}
                </span>
              )}
            </div>
          </div>
        </div>

        {league?.strDescriptionEN && (
          <p className="entity-description">
            {league.strDescriptionEN.length > 600
              ? league.strDescriptionEN.slice(0, 600) + '…'
              : league.strDescriptionEN}
          </p>
        )}

        {/* ── Top FlamePicks Pickers (only for our supported leagues) ── */}
        {isFlamePicks && (
          <section className="entity-section">
            <h2 className="section-title">
              Top Pickers — {SUPPORTED_LEAGUES[leagueId].name}
            </h2>
            {top_pickers?.length > 0 ? (
              <div className="pickers-table-wrap">
                <table className="pickers-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>User</th>
                      <th>W</th>
                      <th>L</th>
                      <th>Total</th>
                      <th>Win%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {top_pickers.map((p, i) => (
                      <tr
                        key={p.user_id}
                        className="picker-row clickable"
                        onClick={() => navigate(`/user/${p.username}`)}
                      >
                        <td className="rank-col">{i + 1}</td>
                        <td className="username-col">{p.username}</td>
                        <td className="win-text">{p.wins}</td>
                        <td className="loss-text">{p.losses}</td>
                        <td>{p.total_picks}</td>
                        <td>{p.win_pct.toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="no-data-text">
                Not enough data yet. Make more picks to appear here!
              </p>
            )}
          </section>
        )}

        {/* ── All Teams ── */}
        {teams?.length > 0 && (
          <section className="entity-section">
            <div className="section-title-row">
              <h2 className="section-title">Teams</h2>
              <input
                type="text"
                placeholder="Filter teams…"
                className="league-team-search"
                value={teamSearch}
                onChange={(e) => setTeamSearch(e.target.value)}
              />
            </div>

            {filteredTeams.length === 0 ? (
              <p className="no-data-text">No teams match "{teamSearch}".</p>
            ) : (
              <div className="league-teams-grid">
                {filteredTeams.map((t) => (
                  <div
                    key={t.idTeam}
                    className="league-team-card clickable"
                    onClick={() => t.idTeam && navigate(`/team/${t.idTeam}`)}
                  >
                    <img
                      src={getLogoPath(t.strTeam)}
                      alt={t.strTeam}
                      className="league-team-badge"
                      onError={(e) => {
                        if (!e.target.dataset.fallback && t.strTeamBadge) {
                          e.target.dataset.fallback = 'true';
                          e.target.src = `${t.strTeamBadge}/tiny`;
                        } else {
                          e.target.style.display = 'none';
                        }
                      }}
                    />
                    <span className="league-team-name">{t.strTeam}</span>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

      </div>
    </BaseLayout>
  );
};

export default LeaguePage;
